'use client';

import { FC } from 'react';
import { useTranslations } from 'next-intl';
import type {
  DefaultSaveStatus,
  KnowledgeSearchStatus
} from '@hooks/use-knowledge-search';
import type { SearchResponseMode } from '../../search/types';

type KnowledgeSearchDefaultControlsProps = {
  mode: SearchResponseMode | null;
  status: KnowledgeSearchStatus;
  termLength: number;
  saveStatus: DefaultSaveStatus;
  onSave: () => void;
  onReset: () => void;
};

/**
 * Quiet, secondary "Use as my default" / "Reset to Recommended" controls
 * (plan Step 6.1). Deliberately ghost/secondary styling so the search
 * surface keeps its one acid-lime accent elsewhere in the app, not here.
 * Never fires on typing, closing, or selecting a result -- only an
 * explicit click saves or resets a preference.
 */
const KnowledgeSearchDefaultControls: FC<
  KnowledgeSearchDefaultControlsProps
> = ({ mode, status, termLength, saveStatus, onSave, onReset }) => {
  const t = useTranslations('knowledge-search');

  if (status === 'loading' || status === 'error' || status === 'offline') {
    return null;
  }

  const canSaveTyped =
    mode === 'typed' && termLength >= 2 && saveStatus !== 'saving';
  const canReset = mode === 'default' && saveStatus !== 'saving';

  if (!canSaveTyped && !canReset) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-2">
      {canSaveTyped ? (
        <button
          type="button"
          onClick={onSave}
          className="min-h-11 text-label text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {saveStatus === 'saved' ? t('editDefault') : t('saveAsDefault')}
        </button>
      ) : null}
      {canReset ? (
        <button
          type="button"
          onClick={onReset}
          className="min-h-11 text-label text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {t('resetDefault')}
        </button>
      ) : null}
    </div>
  );
};

export default KnowledgeSearchDefaultControls;
